function normalizeDegrees(value) {
  return ((value % 360) + 360) % 360;
}

function shortestAngle(target, current) {
  return ((target - current + 540) % 360) - 180;
}

export class HeadingTracker {
  constructor({
    smoothingTimeMs = 180,
    resetAfterMs = 1000,
    jitterWindowMs = 2000,
    jitterMinimumDurationMs = 600,
    jitterPathThreshold = 30,
    jitterReversalThreshold = 24,
    warningClearMs = 2500,
  } = {}) {
    this.smoothingTimeMs = smoothingTimeMs;
    this.resetAfterMs = resetAfterMs;
    this.jitterWindowMs = jitterWindowMs;
    this.jitterMinimumDurationMs = jitterMinimumDurationMs;
    this.jitterPathThreshold = jitterPathThreshold;
    this.jitterReversalThreshold = jitterReversalThreshold;
    this.warningClearMs = warningClearMs;
    this.heading = null;
    this.lastTimestamp = null;
    this.samples = [];
    this.warning = null;
    this.stableSince = null;
  }

  update(reading, timestamp) {
    const rawHeading = typeof reading?.heading === "number" ? reading.heading : NaN;
    const now = Number(timestamp);
    if (!Number.isFinite(rawHeading) || !Number.isFinite(now)) {
      return { heading: this.heading, warning: this.warning };
    }

    const normalized = normalizeDegrees(rawHeading);
    const elapsed = this.lastTimestamp === null ? null : now - this.lastTimestamp;
    if (this.heading === null || elapsed <= 0 || elapsed > this.resetAfterMs) {
      this.heading = normalized;
      this.samples = [];
    } else {
      const weight = 1 - Math.exp(-elapsed / this.smoothingTimeMs);
      this.heading = normalizeDegrees(this.heading + shortestAngle(normalized, this.heading) * weight);
    }
    this.lastTimestamp = now;

    this.samples.push({ heading: normalized, timestamp: now });
    this.samples = this.samples.filter((sample) => now - sample.timestamp <= this.jitterWindowMs);
    const nextWarning = this.classifyWarning(reading, this.isJittery());
    this.updateWarning(nextWarning, now);
    return { heading: this.heading, warning: this.warning };
  }

  isJittery() {
    if (this.samples.length < 5) return false;
    const duration = this.samples.at(-1).timestamp - this.samples[0].timestamp;
    if (duration < this.jitterMinimumDurationMs) return false;
    let clockwise = 0;
    let counterclockwise = 0;
    for (let index = 1; index < this.samples.length; index += 1) {
      const delta = shortestAngle(this.samples[index].heading, this.samples[index - 1].heading);
      if (delta >= 0) clockwise += delta;
      else counterclockwise -= delta;
    }
    const path = clockwise + counterclockwise;
    const reversal = 2 * Math.min(clockwise, counterclockwise);
    return path >= this.jitterPathThreshold && reversal >= this.jitterReversalThreshold;
  }

  classifyWarning(reading, jittery) {
    const accuracy = typeof reading?.compassAccuracy === "number" ? reading.compassAccuracy : NaN;
    if (Number.isFinite(accuracy) && (accuracy < 0 || accuracy > 20)) return "poor";
    if (jittery) return "unstable";
    if (!reading?.hasWebkitHeading && reading?.absolute !== true) return "relative";
    return null;
  }

  updateWarning(nextWarning, now) {
    if (nextWarning) {
      this.warning = nextWarning;
      this.stableSince = null;
      return;
    }
    if (!this.warning) return;
    this.stableSince ??= now;
    if (now - this.stableSince >= this.warningClearMs) {
      this.warning = null;
      this.stableSince = null;
    }
  }
}

export function cameraDirectionFromOrientation(event) {
  const beta = typeof event.beta === "number" ? event.beta : NaN;
  const gamma = typeof event.gamma === "number" ? event.gamma : NaN;
  const webkitHeading = typeof event.webkitCompassHeading === "number" ? event.webkitCompassHeading : NaN;
  const hasWebkitHeading = Number.isFinite(webkitHeading) && webkitHeading >= 0;
  let alpha = typeof event.alpha === "number" ? event.alpha : NaN;
  if (!Number.isFinite(alpha) && hasWebkitHeading) alpha = normalizeDegrees(360 - webkitHeading);
  if (!Number.isFinite(alpha) || !Number.isFinite(beta) || !Number.isFinite(gamma)) return null;

  const toRadians = Math.PI / 180;
  const a = alpha * toRadians;
  const b = beta * toRadians;
  const g = gamma * toRadians;
  const sinA = Math.sin(a);
  const cosA = Math.cos(a);
  const sinB = Math.sin(b);
  const cosB = Math.cos(b);
  const sinG = Math.sin(g);
  const cosG = Math.cos(g);

  // W3C Z-X'-Y'' rotation of the rear-camera vector [0, 0, -1].
  const east = -cosA * sinG - sinA * sinB * cosG;
  const north = -sinA * sinG + cosA * sinB * cosG;
  const up = -cosB * cosG;
  const horizontal = Math.hypot(east, north);
  const rawHeading = horizontal > 1e-6
    ? normalizeDegrees(Math.atan2(east, north) / toRadians)
    : null;

  let heading = rawHeading;
  if (rawHeading !== null && hasWebkitHeading) {
    const uncorrectedHeading = normalizeDegrees(360 - alpha);
    const cameraAxisCorrection = shortestAngle(rawHeading, uncorrectedHeading);
    heading = normalizeDegrees(webkitHeading + cameraAxisCorrection);
  }

  return {
    heading,
    elevation: Math.atan2(up, horizontal) / toRadians,
    absolute: event.absolute === true,
    compassAccuracy: typeof event.webkitCompassAccuracy === "number" ? event.webkitCompassAccuracy : null,
    hasWebkitHeading,
  };
}
